import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { NativeLikeSelect } from "./CocoaSelect";

export function usePagination(rows) {
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  useEffect(() => { setPage(1); }, [rows.length, size]);
  const pages = Math.max(1, Math.ceil(rows.length / size));
  const pageRows = useMemo(() => rows.slice((page - 1) * size, page * size), [rows, page, size]);
  return { pageRows, page, setPage, size, setSize, pages, total: rows.length };
}

export function PaginationBar({ page, setPage, size, setSize, pages, total, testId = "pagination" }) {
  if (total === 0) return null;
  const from = (page - 1) * size + 1;
  const to = Math.min(page * size, total);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#F1EEE7] px-4 py-3" data-testid={testId}>
      <div className="flex items-center gap-2 text-xs text-[#7B6157]">
        <span>Rows per page</span>
        <NativeLikeSelect data-testid={`${testId}-size`} value={size} onChange={(e) => setSize(Number(e.target.value))}
          triggerClass="h-8 w-20 rounded-lg border-[#E5DFD3] bg-white px-2 text-xs text-[#4E342E] focus:ring-2 focus:ring-[#8D6E63]/40">
          {[10, 20, 40, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
        </NativeLikeSelect>
        <span data-testid={`${testId}-range`}>{from} to {to} of {total}</span>
      </div>
      <div className="flex items-center gap-1">
        <button data-testid={`${testId}-prev`} disabled={page <= 1} onClick={() => setPage(page - 1)} aria-label="Previous page"
          className="h-8 w-8 rounded-lg flex items-center justify-center text-[#4E342E] hover:bg-[#F5F2EB] disabled:opacity-30"><ChevronLeft size={15} /></button>
        {Array.from({ length: pages }, (_, i) => i + 1).filter((p) => p === 1 || p === pages || Math.abs(p - page) <= 1)
          .reduce((acc, p, i, arr) => { if (i > 0 && p - arr[i - 1] > 1) acc.push("…"); acc.push(p); return acc; }, [])
          .map((p, i) => p === "…" ? <span key={`e${i}`} className="px-1 text-xs text-[#84675B]">…</span> : (
            <button key={p} data-testid={`${testId}-page-${p}`} onClick={() => setPage(p)}
              className={`h-8 min-w-[32px] rounded-lg px-2 text-xs font-semibold ${p === page ? "bg-[#4E342E] text-white" : "text-[#4E342E] hover:bg-[#F5F2EB]"}`}>{p}</button>
          ))}
        <button data-testid={`${testId}-next`} disabled={page >= pages} onClick={() => setPage(page + 1)} aria-label="Next page"
          className="h-8 w-8 rounded-lg flex items-center justify-center text-[#4E342E] hover:bg-[#F5F2EB] disabled:opacity-30"><ChevronRight size={15} /></button>
      </div>
    </div>
  );
}
